import React, { useContext, useEffect, useState, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { useAnalyticsSources } from '../hooks/useAnalyticsSources';
import axios from 'axios';
import { ReactComponent as ChartIcon } from '../assets/images/icons/chart.svg';
import { ReactComponent as LoyaltyBadgeIcon } from '../assets/images/icons/loyalty-badge.svg';
import { ReactComponent as PlugIcon } from '../assets/images/icons/plug.svg';
import { ReactComponent as GearIcon } from '../assets/images/icons/gear.svg';

const API_BASE = 'https://api.amplifyloyalty.com';

function storeInitial(name) {
  const letter = (name || 'S').trim().slice(0, 1);
  return letter ? letter.toUpperCase() : 'S';
}

function StoreAvatar({ name }) {
  return (
    <div className="main-navigation__info" aria-hidden="true">
      <div className="main-navigation__profile">{storeInitial(name)}</div>
    </div>
  );
}

export default function Navigation() {
  const navigate = useNavigate();
  const { authed, userProfile, getJwtToken, handleSignOut } = useContext(AuthContext);
  const [choosingStore, setChoosingStore] = useState(false);
  const storeSwitcherRef = useRef(null);
  const location = useLocation();
  const { sources: analyticsSources } = useAnalyticsSources();
  const onAnalyticsRoute = location.pathname.startsWith('/app/product-analysis/analytics');
  const activeAnalyticsSourceId = location.pathname.startsWith('/app/product-analysis/analytics/meta-ads')
    ? 'meta-ads'
    : location.pathname.startsWith('/app/product-analysis/analytics/klaviyo')
      ? 'klaviyo'
      : onAnalyticsRoute
        ? 'shopify'
        : null;
  const [analyticsOpen, setAnalyticsOpen] = useState(onAnalyticsRoute);

  useEffect(() => {
    if (onAnalyticsRoute) setAnalyticsOpen(true);
  }, [onAnalyticsRoute]);

  const activeStoreId =
    userProfile?.user?.stores?.active?.id ||
    userProfile?.store?._id ||
    null;

  const authenticatedStores = userProfile?.user?.stores?.authenticated || [];
  const otherStores = authenticatedStores.filter(
    (s) => s?.id && String(s.id) !== String(activeStoreId)
  );

  useEffect(() => {
    if (!choosingStore) return;
    const handleClickOutside = (e) => {
      if (storeSwitcherRef.current && !storeSwitcherRef.current.contains(e.target)) {
        setChoosingStore(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [choosingStore]);

  // Don't render navigation on the forced pricing page
  if (location.pathname === '/app/pricing') {
    return null;
  }

  const handleLogout = async () => {
    setChoosingStore(false);
    await handleSignOut();
    navigate('/login', { replace: true });
  };

  const changeStore = async (store) => {
    try {
      const token = await getJwtToken();
      await axios.post(
        `${API_BASE}/app/auth/switch`,
        { id: store.id, name: store.name },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      window.location.reload();
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      {authed && location.pathname.includes('/app') &&
        <nav className="main-navigation">
          <div className="navigation">
            <div className="navigation__link-wrapper navigation__link-wrapper--stack">
              {(() => {
                const isActiveParent = location.pathname.startsWith('/app/product-analysis');
                return (
                  <>
              <div className="navigation__group">
                <NavLink
                  to="/app/product-analysis/analytics"
                  className={() =>
                    `navigation__link navigation__link--parent${
                      isActiveParent
                        ? ' navigation__link--active'
                        : ''
                    }`
                  }
                >
                  <ChartIcon
                    className={`navigation__icon navigation__icon--chart${
                      isActiveParent ? ' navigation__icon--active' : ''
                    }`}
                    aria-hidden="true"
                  />
                  <span>Product Analysis</span>
                </NavLink>
                <div className="navigation__children">
                {analyticsSources.length > 1 ? (
                  <div className="navigation__sub-group">
                    <NavLink
                      to="/app/product-analysis/analytics"
                      className={() =>
                        `navigation__sub-link${onAnalyticsRoute ? ' navigation__sub-link--active' : ''}`
                      }
                      onClick={() => setAnalyticsOpen(true)}
                    >
                      <div className="navigation__sub-pill navigation__sub-pill--toggle">
                        <span>Sales Analytics</span>
                        <span
                          role="button"
                          tabIndex={0}
                          aria-label={analyticsOpen ? 'Collapse analytics sources' : 'Expand analytics sources'}
                          aria-expanded={analyticsOpen}
                          className={`navigation__sub-caret${analyticsOpen ? ' navigation__sub-caret--open' : ''}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setAnalyticsOpen((v) => !v);
                          }}
                          onKeyDown={(e) => {
                            if (e.key !== 'Enter' && e.key !== ' ') return;
                            e.preventDefault();
                            e.stopPropagation();
                            setAnalyticsOpen((v) => !v);
                          }}
                        />
                      </div>
                    </NavLink>
                    {analyticsOpen &&
                      analyticsSources.map((source) => (
                        <NavLink
                          key={source.id}
                          to={source.path}
                          className={() =>
                            `navigation__sub-link${
                              activeAnalyticsSourceId === source.id ? ' navigation__sub-link--active' : ''
                            }`
                          }
                        >
                          <div className="navigation__sub-pill navigation__sub-pill--nested">
                            {source.logo && <img src={source.logo} alt="" aria-hidden="true" />}
                            <span>{source.name}</span>
                          </div>
                        </NavLink>
                      ))}
                  </div>
                ) : (
                <NavLink
                  to="/app/product-analysis/analytics"
                  className={() =>
                    `navigation__sub-link${onAnalyticsRoute ? ' navigation__sub-link--active' : ''}`
                  }
                >
                  <div className="navigation__sub-pill">Sales Analytics</div>
                </NavLink>
                )}
                <NavLink
                  to="/app/product-analysis/products"
                  className={({ isActive }) =>
                    `navigation__sub-link${isActive ? ' navigation__sub-link--active' : ''}`
                  }
                >
                  <div className="navigation__sub-pill">Products</div>
                </NavLink>
                <NavLink
                  to="/app/product-analysis/briefs"
                  className={({ isActive }) =>
                    `navigation__sub-link${isActive ? ' navigation__sub-link--active' : ''}`
                  }
                >
                  <div className="navigation__sub-pill">Product Briefs</div>
                </NavLink>
                <NavLink
                  to="/app/product-analysis/ai"
                  className={({ isActive }) =>
                    `navigation__sub-link${isActive ? ' navigation__sub-link--active' : ''}`
                  }
                >
                  <div className="navigation__sub-pill">AI Analysis</div>
                </NavLink>
                <NavLink
                  to="/app/product-analysis/rules"
                  className={({ isActive }) =>
                    `navigation__sub-link${isActive ? ' navigation__sub-link--active' : ''}`
                  }
                >
                  <div className="navigation__sub-pill">Product Rules</div>
                </NavLink>
                </div>
              </div>
                  </>
                );
              })()}
            </div>
            <div className="navigation__link-wrapper navigation__link-wrapper--stack">
              {(() => {
                const isActiveParent =
                  location.pathname.startsWith('/app/loyalty') ||
                  location.pathname.startsWith('/app/customers');
                return (
                  <>
              <div className="navigation__group">
                <NavLink
                  to="/app/loyalty/dashboard"
                  className={() =>
                    `navigation__link navigation__link--parent${
                      isActiveParent
                        ? ' navigation__link--active'
                        : ''
                    }`
                  }
                >
                  <LoyaltyBadgeIcon
                    className={`navigation__icon navigation__icon--loyalty${
                      isActiveParent ? ' navigation__icon--active' : ''
                    }`}
                    aria-hidden="true"
                  />
                  <span>Loyalty Program</span>
                </NavLink>
                <div className="navigation__children">
                <NavLink
                  end
                  to="/app/loyalty/dashboard"
                  className={({ isActive }) =>
                    `navigation__sub-link${isActive ? ' navigation__sub-link--active' : ''}`
                  }
                >
                  <div className="navigation__sub-pill">Dashboard</div>
                </NavLink>
                <NavLink
                  to="/app/customers"
                  className={({ isActive }) =>
                    `navigation__sub-link${isActive ? ' navigation__sub-link--active' : ''}`
                  }
                >
                  <div className="navigation__sub-pill">Customers</div>
                </NavLink>
                <NavLink
                  to="/app/loyalty/earn-redeem"
                  className={({ isActive }) =>
                    `navigation__sub-link${isActive ? ' navigation__sub-link--active' : ''}`
                  }
                >
                  <div className="navigation__sub-pill">Earn & Redeem</div>
                </NavLink>
                <NavLink
                  to="/app/loyalty/vip"
                  className={({ isActive }) =>
                    `navigation__sub-link${isActive ? ' navigation__sub-link--active' : ''}`
                  }
                >
                  <div className="navigation__sub-pill">VIP Program</div>
                </NavLink>
                <NavLink
                  to="/app/loyalty/promotions"
                  className={({ isActive }) =>
                    `navigation__sub-link${isActive ? ' navigation__sub-link--active' : ''}`
                  }
                >
                  <div className="navigation__sub-pill">Promotions</div>
                </NavLink>
                <NavLink
                  to="/app/loyalty/giveaways"
                  className={({ isActive }) =>
                    `navigation__sub-link${isActive ? ' navigation__sub-link--active' : ''}`
                  }
                >
                  <div className="navigation__sub-pill">Giveaways</div>
                </NavLink>
                </div>
              </div>
                  </>
                );
              })()}
            </div>
            <a href='/app/integrations' className="navigation__link-wrapper">
              <div className={`navigation__link${location.pathname === '/app/integrations' ? " navigation__link--active" : ""}`}>
                <PlugIcon
                  className={`navigation__icon navigation__icon--plug${
                    location.pathname.includes('/app/integrations') ? ' navigation__icon--active' : ''
                  }`}
                  aria-hidden="true"
                />
                <span>Integrations</span>
              </div>
            </a>
            <a href="/app/settings" className="navigation__link-wrapper">
              <div className={`navigation__link${location.pathname === '/app/settings' ? " navigation__link--active" : ""}`}>
                <GearIcon
                  className={`navigation__icon navigation__icon--gear${
                    location.pathname.includes('/app/settings') ? ' navigation__icon--active' : ''
                  }`}
                  aria-hidden="true"
                />
                <span>Settings</span>
              </div>
            </a>
          </div>
          {userProfile && userProfile.store && userProfile.store.settings?.name && 
            <div
              className="main-navigation__store"
              ref={storeSwitcherRef}
              onClick={() => setChoosingStore((v) => !v)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setChoosingStore((v) => !v);
                }
              }}
            >
              <div className="main-navigation__info">
                <div className="main-navigation__profile">{userProfile.store.settings.name.slice(0,1).toUpperCase()}</div>
              </div>
              <div className="main-navigation__store-name">
                {userProfile.store.settings.name.length < 12 && userProfile.store.settings.name}
                {userProfile.store.settings.name.length > 11 && `${userProfile.store.settings.name.slice(0,12)}...`}
                <i className="fa-solid fa-chevron-up" style={{ marginLeft: '6px', fontSize: '10px' }} />
              </div>
              {choosingStore && 
                <div className="main-navigation__stores" onClick={(e) => e.stopPropagation()}>
                  <div className="main-navigation__stores-user">
                    <StoreAvatar name={userProfile.store.settings.name} />
                    <div className="main-navigation__stores-user-text">
                      <span className="main-navigation__stores-active-name">
                        {userProfile.store.settings.name}
                      </span>
                      <span className="main-navigation__stores-email">{userProfile.user.email}</span>
                    </div>
                  </div>

                  {otherStores.length > 0 && (
                    <>
                      <div className="main-navigation__stores-heading">Accounts</div>
                      <ul className="main-navigation__stores-list">
                        {otherStores.map((str) => (
                          <li key={str.id}>
                            <button
                              type="button"
                              className="main-navigation__extra-store"
                              onClick={() => changeStore(str)}
                            >
                              <StoreAvatar name={str.name} />
                              <span className="main-navigation__extra-store-name">
                                {str.name?.length > 28 ? `${str.name.slice(0, 28)}…` : str.name}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  <div className="main-navigation__stores-footer">
                    <button
                      type="button"
                      className="main-navigation__stores-logout"
                      onClick={handleLogout}
                    >
                      Log out
                    </button>
                  </div>
                </div>  
              }
            </div>
          }
        </nav>
      }
    </>
  )
}
